import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import AdminNodeStore from '../../../stores/admin-node-store';
import { INode, NodeType } from '../../../types/node';
import { Point } from '../../../types/location';
import NodeLocationForm, { NODE_TYPE_LABELS, SELECTABLE_TYPES } from './NodeLocationForm';

interface CreateNodeDialogProps {
    store: AdminNodeStore;
    open: boolean;
    onClose: () => void;
}

const CreateNodeDialog = ({ store, open, onClose }: CreateNodeDialogProps) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<NodeType>(NodeType.Department);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [address, setAddress] = useState('');
    const [coordinates, setCoordinates] = useState<Point | null>(null);

    const handleClose = () => {
        if (loading) return;
        setName('');
        setType(NodeType.Department);
        setDescription('');
        setError('');
        setAddress('');
        setCoordinates(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError('Введите название');
            return;
        }
        setLoading(true);
        setError('');
        const node: INode = {
            name: name.trim(),
            type,
            description: description.trim() || null,
        };
        try {
            await store.createNode(node, address, coordinates);
            handleClose();
        } catch {
            setError('Не удалось создать узел');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !loading) handleSubmit(); }}
        >
            <DialogTitle>Создать</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <TextField
                    label="Название"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!error && !name.trim()}
                    helperText={!name.trim() ? error : ''}
                    autoFocus
                    fullWidth
                    size="small"
                />
                <FormControl fullWidth size="small">
                    <InputLabel>Тип</InputLabel>
                    <Select label="Тип" value={type} onChange={(e) => setType(e.target.value as NodeType)}>
                        {SELECTABLE_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {NODE_TYPE_LABELS[t]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Описание (необязательно)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                />
                <NodeLocationForm
                    address={address}
                    coordinates={coordinates}
                    onChange={(a, c) => { setAddress(a); setCoordinates(c); }}
                />
                {error && name.trim() && <span style={{ color: 'red', fontSize: 13 }}>{error}</span>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Отмена
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading} disableElevation>
                    Создать
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateNodeDialog;
